import { useMutation, useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { usePerson } from "@/shared/hooks/useAppContext";

import type { IntakeTaskId } from "@/shared/constants/taskIds";

interface MarkIntakeStepArgs {
  taskId: IntakeTaskId;
  status: "not_started" | "in_progress" | "completed" | "skipped";
  answers?: Record<string, unknown>;
}

interface IntakeRow {
  task_id: string;
  status: string;
  answers: Record<string, unknown>;
}

export function useMarkIntakeStep() {
  const person = usePerson();
  const estateId = person?.estate_id;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, status, answers }: MarkIntakeStepArgs) => {
      if (!estateId) throw new Error("No estate");
      const { error } = await supabase.functions.invoke("onboarding/intake-step", {
        body: { estate_id: estateId, task_id: taskId, status, answers },
      });
      if (error) throw error;
    },
    onMutate: async ({ taskId, status, answers }) => {
      const key = ["intakeStatus", estateId];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<IntakeRow[]>(key);

      queryClient.setQueryData<IntakeRow[]>(key, (old) => {
        const rows = [...(old ?? [])];
        const idx = rows.findIndex((r) => r.task_id === taskId);
        const newRow: IntakeRow = { task_id: taskId, status, answers: answers ?? {} };
        if (idx >= 0) {
          rows[idx] = newRow;
        } else {
          rows.push(newRow);
        }
        return rows;
      });

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(["intakeStatus", estateId], context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["intakeStatus", estateId] });
    },
  });
}
